import { ReactNode, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import './Drawer.scss'
import classNames from 'classnames'
import Icon from 'shared/Icon'
import { Button } from 'shared/Button'

type DrawerProps = {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  copyContent?: string | null
  feedbackLink?: string | null
}

export function Drawer({ isOpen, onClose, title, children, copyContent, feedbackLink }: Readonly<DrawerProps>) {
  const [copied, setCopied] = useState(false)

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        ;(document.activeElement as HTMLElement)?.blur?.()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleCopy = () => {
    if (!copyContent) return
    navigator.clipboard.writeText(copyContent).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return ReactDOM.createPortal(
    <div className={classNames('Drawer', { 'Drawer--visible': isOpen })} onClick={onClose}>
      <div
        className={classNames('Drawer__panel', { 'Drawer__panel--open': isOpen })}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="Drawer__header">
          <span className="Drawer__title">{title}</span>
          <div className="Drawer__header-actions">
            {feedbackLink && (
              <Button
                appearance="solid"
                color="primary"
                onClick={() => window.open(feedbackLink, '_blank', 'noopener,noreferrer')}
              >
                Share your feedback
              </Button>
            )}
            {copyContent && (
              <Button
                appearance="outline"
                onClick={handleCopy}
                tooltipContent={copied ? 'Copied!' : 'Copy to clipboard'}
              >
                <Icon name="Copy" />
              </Button>
            )}
            <Button appearance="outline" onClick={onClose}>
              <Icon name="Cross" />
            </Button>
          </div>
        </div>
        <div className="Drawer__content">{children}</div>
      </div>
    </div>,
    document.body
  )
}
